import React from "react";
import "./smile-pages.css";

export default function HowDoesSmilingLowerYourBloodPressure() {
  return (
    <div className="container-box mt-5 mb-5 smile-page">
      <h1>How does Smiling Lower Your Blood Pressure?</h1>

      <p>
        Smiling helps lower your blood pressure by triggering <strong>relaxation responses in the brain and body</strong>, which
        <strong> calm</strong> your cardiovascular system. Whether genuine or even forced, smiling activates neural pathways
        that <strong>reduce stress</strong>, <strong>ease tension in blood vessels</strong>, and <strong>promote better blood flow</strong>, all of which
        <strong> contribute to lowering blood pressure</strong>.
      </p>

      <p>Here’s how the process works, backed by science:</p>

      <hr />

      {/* -------------------- 1 -------------------- */}
      <h3>🧠 1. Smiling Activates the Parasympathetic Nervous System (Rest & Digest)</h3>

      <p>
        Smiling — especially when paired with positive <strong>emotion</strong> — stimulates your{" "}
        <strong>parasympathetic nervous system</strong>, which is responsible for:
      </p>

      <ul>
        <li>Slowing the heart rate</li>
        <li>Relaxing blood vessels</li>
        <li>Reducing the body’s “fight-or-flight” stress response</li>
      </ul>

      <p>
        As a result, <strong>blood pressure decreases</strong>, sometimes within minutes of smiling or laughing.
      </p>

      <p>
        📚 <strong>Study: </strong>  
        Kraft & Pressman (2012) found that participants who smiled during stressful tasks recovered{" "}
        <strong>more quickly</strong> and had <strong>lower heart rates</strong> than those with neutral expressions. This cardiovascular
        benefit is linked to <strong>lower blood pressure</strong> over time.
      </p>

      <hr />

      {/* -------------------- 2 -------------------- */}
      <h3>💓 2. Smiling Reduces Stress Hormones Like Cortisol and Adrenaline</h3>

      <p>When you're stressed, your body releases <strong>cortisol</strong> and <strong>adrenaline</strong>, which:</p>

      <ul>
        <li>Constrict blood vessels</li>
        <li>Raise heart rate</li>
        <li>Increase blood pressure</li>
      </ul>

      <p>
        Smiling lowers these stress hormones, helping your cardiovascular system{" "}
        <strong>relax and function more efficiently</strong>.
      </p>

      <p>
        📚 <strong>Source: </strong>  
        Pressman & Cohen (2005) found that <strong>positive emotional expressions</strong>, like smiling and laughter, help
        protect against the cardiovascular effects of stress.
      </p>

      <hr />

      {/* -------------------- 3 -------------------- */}
      <h3>💨 3. Smiling Promotes Deep, Calming Breathing</h3>

      <p>Smiling often accompanies <strong>deep or rhythmic breathing</strong>, which:</p>

      <ul>
        <li>Increases oxygen intake</li>
        <li>Signals <strong>calm</strong> to the brain</li>
        <li>Helps the <strong>heart beat</strong> more efficiently</li>
      </ul>

      <p>
        This breathing pattern supports <strong>blood pressure regulation</strong>, especially when combined with other calming
        behaviors like mindfulness or laughter.
      </p>

      <hr />

      {/* -------------------- 4 -------------------- */}
      <h3>🔄 4. Smiling Improves Vascular Function</h3>

      <p>
        Laughter and smiling increase <strong>blood vessel flexibility</strong> (vasodilation), which:
      </p>

      <ul>
        <li>Enhances <strong>circulation</strong></li>
        <li>Reduces <strong>vascular resistance</strong></li>
        <li>Leads to <strong>lower systolic and diastolic pressure</strong></li>
      </ul>

      <p>
        📚 <strong>Study: </strong>  
        A 2005 study at the University of Maryland Medical Center found that watching a funny movie improved
        <strong> blood vessel function</strong>, <strong>boosting blood flow by up to 22%</strong>, while stressful clips narrowed vessels —{" "}
        <em>a risk factor for hypertension.</em>
      </p>

      <hr />

      {/* -------------------- SUMMARY -------------------- */}
      <h3>✅ Summary: How Smiling Lowers Blood Pressure</h3>

      <table className="summary-table">
        <thead>
          <tr>
            <th>Smile Effect</th>
            <th>Blood Pressure Benefit</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Activates parasympathetic nervous system</td>
            <td>Slows heart rate, relaxes vessels</td>
          </tr>
          <tr>
            <td>Reduces cortisol and adrenaline</td>
            <td>Lowers stress-induced pressure spikes</td>
          </tr>
          <tr>
            <td>Improves breathing rhythm</td>
            <td>Supports cardiovascular regulation</td>
          </tr>
          <tr>
            <td>Increases blood vessel dilation</td>
            <td>Enhances circulation and lowers vascular tension</td>
          </tr>
        </tbody>
      </table>

      <hr />

      {/* -------------------- KEYS TO REMEMBER -------------------- */}
      <h3>🔑 Keys to Remember</h3>

      <p>
        <strong>Smiling is a natural, drug-free way to help lower your blood pressure.</strong> Smiling relaxes your
        body, calms your mind, and supports healthy heart function — especially when practiced regularly.
      </p>

      <p>
        Even a small smile during stressful moments can have measurable cardiovascular benefits over time.{" "}
      </p>
      <p>
        <strong>That Smile App is here to help lower your blood pressure.</strong>
      </p>
    </div>
  );
}
